import { useEffect, useState } from 'react';
import type { PpLiveFeed } from '../auth/api.ts';

export interface LiveTable {
  tableId: string;
  name: string;
  open: boolean | null;
  image: string | null;
  dealer: string | null;
  minBet: number | null;
  maxBet: number | null;
  seated: number | null;
  availableSeats: number | null;
  recent: string[];
  newTable: boolean;
  tableType: string | null;
  shoe: { banker: number; player: number; tie: number } | null;
}

function object(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

function number(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

/** The PP feed sends a full object for each table. Ignore availability/ping messages. */
export function parseLiveTable(value: unknown): LiveTable | null {
  const outer = object(value);
  const row = object(outer?.data) ?? outer;
  if (!row || !/^[A-Za-z0-9_-]{1,32}$/.test(String(row.tableId ?? ''))) return null;
  const limits = object(row.tableLimits);
  const dealer = object(row.dealer);
  const history = Array.isArray(row.last20Results) ? row.last20Results
    : Array.isArray(row.gameResult) ? row.gameResult : [];
  const recent = history.slice(0, 5).map((item) => {
    const result = object(item);
    const value = result?.winner ?? result?.result;
    return typeof value === 'string' || typeof value === 'number' ? String(value).slice(0, 40) : '';
  }).filter(Boolean);
  const summary = object(row.baccaratShoeSummary);
  const banker = Number(summary?.bankerWinCounter);
  const player = Number(summary?.playerWinCounter);
  const tie = Number(summary?.tieCounter);
  const image = typeof row.tableImage === 'string' && /^https:\/\//i.test(row.tableImage)
    ? row.tableImage : null;
  return {
    tableId: String(row.tableId),
    name: typeof row.tableName === 'string' ? row.tableName.slice(0, 100) : '',
    open: typeof row.tableOpen === 'boolean' ? row.tableOpen : null,
    image,
    dealer: typeof dealer?.name === 'string' ? dealer.name.slice(0, 80) : null,
    minBet: number(limits?.minBet), maxBet: number(limits?.maxBet),
    seated: number(row.totalSeatedPlayers), availableSeats: number(row.availableSeats),
    recent,
    newTable: row.newTable === true,
    tableType: typeof row.tableType === 'string' ? row.tableType.slice(0, 40) : null,
    shoe: summary && [banker, player, tie].every(Number.isFinite)
      ? { banker, player, tie } : null,
  };
}

/** Matches PP's dgaAPI.js available/subscribe wire messages (delta disabled). */
export function dgaMessages(feed: PpLiveFeed): string[] {
  return [JSON.stringify({ type: 'available', casinoId: feed.casinoId }),
    ...feed.tables.map(({ tableId }) => JSON.stringify({ type: 'subscribe', key: tableId,
      casinoId: feed.casinoId, currency: feed.currency }))];
}

/** One socket per open lobby, with bounded reconnect and no stale table state. */
export function usePpLiveTables(feed: PpLiveFeed | undefined): Record<string, LiveTable> {
  const [tables, setTables] = useState<Record<string, LiveTable>>({});
  useEffect(() => {
    if (!feed?.tables.length) { setTables({}); return; }
    setTables({});
    const ids = new Set(feed.tables.map((t) => t.tableId));
    let closed = false;
    let attempts = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let socket: WebSocket | undefined;
    const connect = () => {
      if (closed) return;
      try {
        socket = new WebSocket(`wss://${feed.server}/ws`);
      } catch {
        retry = setTimeout(connect, Math.min(30_000, 1000 * 2 ** Math.min(attempts++, 5)));
        return;
      }
      socket.onopen = () => {
        attempts = 0;
        for (const message of dgaMessages(feed)) socket?.send(message);
      };
      socket.onmessage = (event) => {
        try {
          const table = parseLiveTable(JSON.parse(String(event.data)));
          if (!closed && table && ids.has(table.tableId)) {
            setTables((old) => ({ ...old, [table.tableId]: table }));
          }
        } catch { /* malformed provider data does not break the lobby */ }
      };
      socket.onclose = () => {
        if (!closed) {
          setTables({});
          retry = setTimeout(connect, Math.min(30_000, 1000 * 2 ** Math.min(attempts++, 5)));
        }
      };
      socket.onerror = () => socket?.close();
    };
    connect();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      socket?.close();
    };
  }, [feed]);
  return tables;
}
